import { PersonalDetailsType } from '@/bundle/shared/components/UserDetails/UserPersonalDetails';
import { useFormik } from 'formik';
import { FormValues, getChangedFormValues } from '@/helpers/formHelpers/formHelpers';
import { Box } from '@/components/Box/Box';
import { Button } from '@/components/Button/Button';
import { useMutation } from '@tanstack/react-query';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { EDIT_OPCO_VALIDATION_SCHEMA, getEditOpcoInitialValues } from './const';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { editSelfUserDetails } from '@/bundle/shared/api';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { PERSONAL_INFORMATION_UPDATED_MESSAGE } from '@/const/shared';

interface EditOpcoPersonalDetailsFormType {
  personalDetails: PersonalDetailsType;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditOpcoPersonalDetailsForm = ({
  personalDetails,
  onClose,
  onSuccess,
}: EditOpcoPersonalDetailsFormType) => {
  const { mutate, isPending } = useMutation({
    mutationKey: ['edit_opco_personal_details'],
    mutationFn: (values: FormValues) => {
      return editSelfUserDetails(values);
    },
    onSuccess(response) {
      if (response?.error) {
        showSnackbarResponseError(response?.error);

        return;
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.ProfileEditPersonalInfoSuccess);

      showSnackbar(PERSONAL_INFORMATION_UPDATED_MESSAGE);
      onSuccess();
      onClose();
    },
  });

  const formik = useFormik({
    initialValues: getEditOpcoInitialValues(personalDetails),
    validationSchema: EDIT_OPCO_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      const payload = getChangedFormValues<FormValues>(values, personalDetails as FormValues);

      if (!payload) {
        onClose();

        return;
      }

      mutate(payload);
    },
  });

  return (
    <Box flexDirection='column' rowGap='14px'>
      <FormikForm value={formik}>
        <FormikInput name='first_name' label='First Name*' placeholder='Enter First Name' />
        <FormikInput name='middle_name' label='Middle Name' placeholder='Enter Middle Name' />
        <FormikInput name='last_name' label='Last Name*' placeholder='Enter Last Name' />
        <ButtonGroup marginTop='14px'>
          <Button variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' isLoading={isPending}>
            Save
          </Button>
        </ButtonGroup>
      </FormikForm>
    </Box>
  );
};
